import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { FiArrowLeft, FiCheck, FiX, FiRefreshCw } from 'react-icons/fi';
import toast from 'react-hot-toast';

const Assentos = () => {
  const { viagemId } = useParams();
  const navigate = useNavigate();
  const [assentos, setAssentos] = useState([]);
  const [passageiros, setPassageiros] = useState([]);
  const [viagem, setViagem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAssento, setSelectedAssento] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [action, setAction] = useState('reservar'); // reservar, trocar, liberar
  const [pisoVisivel, setPisoVisivel] = useState('inferior'); // inferior | superior

  useEffect(() => {
    fetchData();
  }, [viagemId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [assentosRes, viagemRes, passageirosRes] = await Promise.all([
        api.get(`/api/assentos/viagem/${viagemId}`),
        api.get(`/api/viagens/${viagemId}`),
        api.get('/api/passageiros')
      ]);

      setAssentos(assentosRes.data || []);
      setViagem(viagemRes.data);
      setPassageiros(passageirosRes.data || []);
      
      // Se não há assentos, a API deve ter criado automaticamente, mas vamos garantir
      if (!assentosRes.data || assentosRes.data.length === 0) {
        // Aguardar um pouco e tentar recarregar
        setTimeout(() => {
          api.get(`/api/assentos/viagem/${viagemId}`)
            .then(res => {
              if (res.data && res.data.length > 0) {
                setAssentos(res.data);
              }
            })
            .catch(() => {
              // Ignorar erro
            });
        }, 1000);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'disponivel':
        return 'bg-green-500 hover:bg-green-600';
      case 'reservado':
        return 'bg-yellow-500 hover:bg-yellow-600';
      case 'ocupado':
        return 'bg-red-500 hover:bg-red-600';
      default:
        return 'bg-gray-300';
    }
  };

  const handleAssentoClick = (assento) => {
    if (assento.status === 'ocupado') {
      setSelectedAssento(assento);
      setAction('trocar');
      setModalOpen(true);
    } else if (assento.status === 'reservado') {
      setSelectedAssento(assento);
      setAction('liberar');
      setModalOpen(true);
    } else {
      setSelectedAssento(assento);
      setAction('reservar');
      setModalOpen(true);
    }
  };

  const handleReservar = async (passageiroId) => {
    try {
      await api.post('/api/assentos/reservar', {
        viagem_id: viagemId,
        assento_id: selectedAssento.id,
        passageiro_id: passageiroId
      });
      toast.success('Assento reservado com sucesso');
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao reservar assento');
    }
  };

  const handleTrocar = async (novoAssentoId, passageiroId) => {
    try {
      await api.post('/api/assentos/trocar', {
        viagem_id: viagemId,
        assento_antigo_id: selectedAssento.id,
        assento_novo_id: novoAssentoId,
        passageiro_id: passageiroId
      });
      toast.success('Assento trocado com sucesso');
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao trocar assento');
    }
  };

  const handleLiberar = async () => {
    try {
      await api.post('/api/assentos/liberar', {
        assento_id: selectedAssento.id
      });
      toast.success('Assento liberado com sucesso');
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao liberar assento');
    }
  };

  const handleMarcarReservado = async () => {
    try {
      await api.post('/api/assentos/marcar-reservado', {
        assento_id: selectedAssento.id
      });
      toast.success('Assento marcado como reservado');
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao marcar assento');
    }
  };

  // Organizar assentos por fileira e lado
  const assentosPorFileira = {};
  if (assentos && assentos.length > 0) {
    assentos.forEach(assento => {
      const key = `${assento.fileira}`;
      if (!assentosPorFileira[key]) {
        assentosPorFileira[key] = [];
      }
      assentosPorFileira[key].push(assento);
    });
  }

  // Ordenar fileiras alfabeticamente
  const fileiras = Object.keys(assentosPorFileira).sort((a, b) => a.localeCompare(b));

  const isDoubleDeckerPisos = viagem?.tipo_onibus === 'double_decker_60' || viagem?.tipo_onibus === 'double_decker_56';
  const fileirasInferior = isDoubleDeckerPisos
    ? fileiras.filter((fileira) =>
        assentosPorFileira[fileira]?.some((a) => a.numero >= 45)
      )
    : fileiras;
  const fileirasSuperior = isDoubleDeckerPisos
    ? fileiras.filter((fileira) =>
        assentosPorFileira[fileira]?.some((a) => a.numero <= 44)
      )
    : [];
  const fileirasParaRender = isDoubleDeckerPisos
    ? (pisoVisivel === 'inferior' ? fileirasInferior : fileirasSuperior)
    : fileiras;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/viagens')}
            className="p-2 hover:bg-gray-800 rounded-lg text-white"
          >
            <FiArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">Mapa de Assentos</h1>
            <p className="text-white mt-1">{viagem?.titulo}</p>
          </div>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors"
        >
          <FiRefreshCw size={18} />
          Atualizar
        </button>
      </div>

      {/* Legenda */}
      <div className="bg-sky-50 rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-6">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-green-500 rounded"></div>
            <span className="text-sm text-gray-800">Disponível</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-yellow-500 rounded"></div>
            <span className="text-sm text-gray-800">Reservado</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-red-500 rounded"></div>
            <span className="text-sm text-gray-800">Ocupado</span>
          </div>
        </div>
      </div>

      {/* Mapa de Assentos */}
      <div className="bg-sky-50 rounded-lg shadow p-6">
        <div className="space-y-6">
          {fileiras.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">
                Assentos ainda não foram criados para esta viagem.
              </p>
              <button
                onClick={fetchData}
                className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors flex items-center gap-2 mx-auto"
              >
                <FiRefreshCw size={18} />
                Recarregar
              </button>
            </div>
          ) : (
            <>
              {/* Toggle de piso para double decker 60/56 */}
              {isDoubleDeckerPisos && (
                <div className="flex justify-center mb-4">
                  <div className="inline-flex rounded-full bg-sky-100 p-1">
                    <button
                      type="button"
                      onClick={() => setPisoVisivel('inferior')}
                      className={`px-4 py-1 text-xs font-semibold rounded-full transition-colors ${
                        pisoVisivel === 'inferior'
                          ? 'bg-white text-sky-900 shadow-sm'
                          : 'text-sky-800 hover:bg-sky-200'
                      }`}
                    >
                      Piso Inferior
                    </button>
                    <button
                      type="button"
                      onClick={() => setPisoVisivel('superior')}
                      className={`px-4 py-1 text-xs font-semibold rounded-full transition-colors ${
                        pisoVisivel === 'superior'
                          ? 'bg-white text-sky-900 shadow-sm'
                          : 'text-sky-800 hover:bg-sky-200'
                      }`}
                    >
                      Piso Superior
                    </button>
                  </div>
                </div>
              )}

              <div className="flex justify-center mb-4">
                <div className="text-center">
                  <div className="w-32 h-8 bg-gray-300 rounded mb-2 flex items-center justify-center text-sm font-bold">
                    ENTRADA
                  </div>
                </div>
              </div>

              {/* Lista de fileiras para o piso selecionado (ou todos nos outros tipos) */}
              {fileirasParaRender.map((fileira) => {
              const assentosFileira = assentosPorFileira[fileira].sort((a, b) => a.numero - b.numero);
              const ladoE = assentosFileira.filter(a => a.lado === 'E');
              const ladoD = assentosFileira.filter(a => a.lado === 'D');

              return (
                <div key={fileira} className="flex items-center gap-4">
                  <div className="w-12 text-center font-bold text-gray-700">{fileira}</div>
                  <div className="flex-1 flex gap-2">
                    <div className="flex-1 flex gap-2 justify-end">
                      {ladoE.map((assento) => (
                        <button
                          key={assento.id}
                          onClick={() => handleAssentoClick(assento)}
                          className={`w-12 h-12 rounded text-white font-bold text-sm ${getStatusColor(assento.status)} transition-colors relative group`}
                          title={`Assento ${assento.numero} - Fileira ${assento.fileira}${assento.lado}${assento.passageiro_nome ? ` - ${assento.passageiro_nome}` : ' - Livre'}`}
                        >
                          {assento.numero}
                          {assento.passageiro_nome && (
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-black text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                              {assento.passageiro_nome}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                    <div className="w-8"></div>
                    <div className="flex-1 flex gap-2">
                      {ladoD.map((assento) => (
                        <button
                          key={assento.id}
                          onClick={() => handleAssentoClick(assento)}
                          className={`w-12 h-12 rounded text-white font-bold text-sm ${getStatusColor(assento.status)} transition-colors relative group`}
                          title={`Assento ${assento.numero} - Fileira ${assento.fileira}${assento.lado}${assento.passageiro_nome ? ` - ${assento.passageiro_nome}` : ' - Livre'}`}
                        >
                          {assento.numero}
                          {assento.passageiro_nome && (
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-black text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                              {assento.passageiro_nome}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
              })}
            </>
          )}
        </div>
      </div>

      {/* Modal de Ações */}
      {modalOpen && selectedAssento && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {action === 'reservar' && 'Reservar Assento'}
              {action === 'trocar' && 'Trocar Assento'}
              {action === 'liberar' && 'Liberar Assento'}
            </h2>

            <p className="text-gray-700 mb-4">
              Assento {selectedAssento.numero} - Fileira {selectedAssento.fileira}
            </p>

            {action === 'reservar' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Selecione o passageiro</label>
                <select
                  id="passageiro"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4"
                >
                  <option value="">Selecione...</option>
                  {passageiros
                    .filter(p => p.viagem_id == viagemId || !p.viagem_id)
                    .map(p => (
                      <option key={p.id} value={p.id}>{p.nome} - {p.cpf}</option>
                    ))}
                </select>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const passageiroId = document.getElementById('passageiro').value;
                      if (passageiroId) {
                        handleReservar(passageiroId);
                      } else {
                        toast.error('Selecione um passageiro');
                      }
                    }}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Reservar
                  </button>
                  <button
                    onClick={handleMarcarReservado}
                    className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
                  >
                    Marcar Reservado
                  </button>
                  <button
                    onClick={() => setModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <FiX size={20} />
                  </button>
                </div>
              </div>
            )}

            {action === 'trocar' && (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Passageiro atual: {selectedAssento.passageiro_nome || 'N/A'}
                </p>
                <label className="block text-sm font-medium text-gray-700 mb-2">Novo assento</label>
                <select
                  id="novoAssento"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4"
                >
                  <option value="">Selecione...</option>
                  {assentos
                    .filter(a => a.status === 'disponivel' && a.id !== selectedAssento.id)
                    .map(a => (
                      <option key={a.id} value={a.id}>
                        Assento {a.numero} - Fileira {a.fileira}
                      </option>
                    ))}
                </select>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const novoAssentoId = document.getElementById('novoAssento').value;
                      if (novoAssentoId) {
                        handleTrocar(novoAssentoId, selectedAssento.passageiro_id);
                      } else {
                        toast.error('Selecione um novo assento');
                      }
                    }}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Trocar
                  </button>
                  <button
                    onClick={() => setModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {action === 'liberar' && (
              <div className="flex gap-2">
                <button
                  onClick={handleLiberar}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Liberar
                </button>
                <button
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Assentos;
